import { Link, useNavigate } from 'react-router-dom'
import { Button, Progress, Tooltip, Popconfirm } from 'antd'
import { EyeOutlined, ReloadOutlined, DownloadOutlined, DeleteOutlined } from '@ant-design/icons'
import { AdvisoryCallStatus } from '@/features/advisory-ai/types'
import { getAdvisoryAiReportTypeDisplayName } from '@/features/advisory-ai/api'
import type { AdvisoryAiOrgReportRow } from '@/features/advisory-ai/types'
import { RevaluationMissingBadge } from '@/features/advisory-ai/components/RevaluationMissingBadge'
import { createAdvisoryAccessGuard } from '@/features/advisory-ai/access'
import dayjs from 'dayjs'
import {
  getCallStatus,
  getDaysAway,
  getMeetingDateDisplay,
  isMeetingFinished,
  isRealMeetingDate,
  getStepProgress,
  formatCallDateRange,
} from '../utils'

type CallCardProps = {
  row: AdvisoryAiOrgReportRow
  orgId: string
  isRefreshing: boolean
  isDeleting: boolean
  onRefresh: () => void
  onDelete: (row: AdvisoryAiOrgReportRow) => void
  onRefreshToast: () => void
  /** Called instead of the action when this user is on none of the meeting's organisations. */
  onBlocked: () => void
}

export function CallCard({
  row,
  orgId,
  isRefreshing,
  isDeleting,
  onRefresh,
  onDelete,
  onRefreshToast,
  onBlocked,
}: CallCardProps) {
  const navigate = useNavigate()
  const status = getCallStatus(row)
  const hasRealMeetingDate = isRealMeetingDate(row.meetingDate)
  const meetingFinished = isMeetingFinished(row.meetingDate)
  const callDate = getMeetingDateDisplay(row.meetingDate)
  const daysAway = hasRealMeetingDate ? getDaysAway(row.meetingDate) : ''
  const stepProgress = getStepProgress(row)
  const progressPercent = (stepProgress.current / stepProgress.total) * 100

  // Values mirror GROUP_STATUS_META in consolidated/utils.ts, which styles the
  // same four statuses on the consolidated list. Keep the two in step.
  const borderColor =
    status === 'needsAttention'
      ? 'border-l-red-500'
      : status === 'inProgress'
        ? 'border-l-orange-500'
        : status === 'generating'
          ? 'border-l-blue-500'
          : 'border-l-green-500'
  const tagColor =
    status === 'needsAttention'
      ? 'bg-red-50 text-red-700 border-red-200'
      : status === 'inProgress'
        ? 'bg-orange-50 text-orange-700 border-orange-200'
        : status === 'generating'
          ? 'bg-blue-50 text-blue-700 border-blue-200'
          : 'bg-green-50 text-green-700 border-green-200'
  const progressColor =
    status === 'needsAttention'
      ? '#ef4444'
      : status === 'inProgress'
        ? '#f97316'
        : status === 'generating'
          ? '#3b82f6'
          : '#22c55e'

  const reportTypes = row.reports.map((r) => getAdvisoryAiReportTypeDisplayName(r)).join(', ')
  const headlinerTeaser = (row.headlinerSummary ?? '').trim()
  const statusUpper = String(row.status || '').toUpperCase()
  const canStart = statusUpper === AdvisoryCallStatus.PENDING
  const canContinue =
    statusUpper === AdvisoryCallStatus.STARTED_BRIEF || statusUpper === AdvisoryCallStatus.COMPLETED_BRIEF
  const canReview = statusUpper === AdvisoryCallStatus.COMPLETED_EXPORT && !meetingFinished

  const reportPath = `/adviceprep/org/${orgId}/report/${row.advisoryCallId}`

  // The row stays visible either way. What changes is where its controls lead: for a user who
  // is on none of this meeting's organisations they explain themselves instead of acting.
  const { canInteract, blockedTooltip, guard } = createAdvisoryAccessGuard(row.canInteract, onBlocked)
  const openReport = (path: string) => guard(() => navigate(path))

  return (
    <div className={`bg-white rounded-lg border border-gray-200 ${borderColor} border-l-4 p-4 mb-4`}>
      <div className="flex-1">
        <div className="flex items-center gap-3 mb-2 flex-wrap">
          <div className="bg-gray-100 px-3 py-1.5 rounded text-sm text-gray-700 font-medium border-l-4 border-gray-300">
            {callDate}
          </div>
          <div className="text-sm text-gray-700 font-medium">
            {hasRealMeetingDate && row.meetingDate ? `${dayjs(row.meetingDate).format('h:mm A')} — ` : ''}{reportTypes || 'No report type'}
          </div>
          <div className="flex items-center gap-2 ml-auto">
            {row.revaluationMissing ? <RevaluationMissingBadge /> : null}
            {daysAway && (
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${tagColor}`}>
                {daysAway}
              </span>
            )}
            {stepProgress.label && <span className="text-xs text-gray-500">{stepProgress.label}</span>}
            <Tooltip title={canInteract ? 'View meeting' : blockedTooltip}>
              {canInteract ? (
                <Link to={reportPath}>
                  <Button type="text" icon={<EyeOutlined />} aria-label="View meeting" size="small" />
                </Link>
              ) : (
                <Button
                  type="text"
                  icon={<EyeOutlined />}
                  aria-label="View meeting"
                  size="small"
                  className="opacity-50"
                  onClick={onBlocked}
                />
              )}
            </Tooltip>
            <Tooltip title="Refresh meeting status">
              <Button
                type="text"
                icon={<ReloadOutlined />}
                loading={isRefreshing}
                size="small"
                onClick={() => {
                  onRefresh()
                  onRefreshToast()
                }}
              />
            </Tooltip>
            {canInteract ? (
              <Popconfirm
                title="Delete meeting"
                description="Are you sure you want to delete this meeting? This cannot be undone."
                onConfirm={() => onDelete(row)}
                okText="Delete"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
              >
                <Tooltip title="Delete meeting">
                  <Button
                    type="text"
                    icon={<DeleteOutlined />}
                    loading={isDeleting}
                    size="small"
                    danger
                    aria-label="Delete meeting"
                  />
                </Tooltip>
              </Popconfirm>
            ) : (
              // No Popconfirm: asking someone to confirm a delete that will then be refused is
              // worse than refusing up front.
              <Tooltip title={blockedTooltip}>
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  size="small"
                  danger
                  aria-label="Delete meeting"
                  className="opacity-50"
                  onClick={onBlocked}
                />
              </Tooltip>
            )}
          </div>
        </div>
        <div className="text-sm text-gray-600 mb-2">{formatCallDateRange(row)}</div>
        {headlinerTeaser ? (
          <p className="text-sm text-gray-600 italic line-clamp-2 mb-2" title={headlinerTeaser}>
            {headlinerTeaser}
          </p>
        ) : null}
        <Progress
          percent={progressPercent}
          strokeColor={progressColor}
          showInfo={false}
          size="small"
          className="mb-3"
        />
      </div>
      <div className="flex items-center gap-2">
        {(canStart || canContinue) && (
          <Tooltip title={blockedTooltip}>
            <Button type="primary" onClick={() => openReport(reportPath)}>
              {canStart ? 'Start Prep →' : 'Continue →'}
            </Button>
          </Tooltip>
        )}
        {canReview && (
          <>
            <Tooltip title={blockedTooltip}>
              <Button onClick={() => openReport(reportPath)}>Review</Button>
            </Tooltip>
            <Tooltip title={blockedTooltip}>
              <Button
                type="primary"
                style={{ backgroundColor: '#22c55e', borderColor: '#22c55e' }}
                icon={<DownloadOutlined />}
                onClick={() => openReport(`${reportPath}?step=4`)}
              >
                Present
              </Button>
            </Tooltip>
          </>
        )}
      </div>
    </div>
  )
}
